<template>
    <form-validation ref="exposedValidation">
        <!-- team information -->
        <flex-container>
            <flex-container
                :screen-sizes="{
                    laptop: 2,
                }"
            >
                <data-group>
                    <data-label>
                        Vestigingsinformatie
                    </data-label>
                    <data-value>
                        Geef de informatie op
                    </data-value>
                </data-group>
            </flex-container>

            <!-- left side of form -->
            <flex-container
                :screen-sizes="{
                    tablet: 4,
                    laptop: 3,
                }"
            >
                <input-group v-model="form.name" :schema="string().required()">
                    <template #label>
                        <input-label>
                            Vestigingsnaam
                        </input-label>
                    </template>
                    <template #input>
                        <text-input />
                    </template>
                </input-group>

                <flex-container>
                    <flex-container :screen-sizes="{ tablet: 5 }">
                        <input-group v-model="form.zipcode" :schema="string().required()">
                            <template #label>
                                <input-label>
                                    Postcode
                                </input-label>
                            </template>
                            <template #input>
                                <text-input @focusout="searchForZipCode" />
                            </template>
                        </input-group>
                    </flex-container>
                    <flex-container
                        :screen-sizes="{
                            tablet: 3,
                        }"
                    >
                        <input-group v-model="form.houseNumber" :schema="string().required()">
                            <template #label>
                                <input-label>
                                    Huisnummer
                                </input-label>
                            </template>
                            <template #input>
                                <text-input @focusout="searchForZipCode" />
                            </template>
                        </input-group>
                    </flex-container>
                    <flex-container
                        :screen-sizes="{ tablet: 3 }"
                        align-content="flex-end"
                        justify-content="flex-end"
                    >
                        <button-block-icon
                            :disabled="disableRefresh"
                            :icon="RefreshIcon"
                            @click="searchForZipCode"
                        />
                    </flex-container>
                </flex-container>

                <input-group v-model="form.city" :schema="string().required()">
                    <template #label>
                        <input-label>
                            Plaats
                        </input-label>
                    </template>
                    <template #input>
                        <text-input :disabled="disableAddress" />
                    </template>
                </input-group>

                <input-group
                    v-if="hasWinckBranding()"
                    v-model="form.website"
                    :schema="string().url().required()"
                >
                    <template #label>
                        <input-label>
                            Website
                        </input-label>
                    </template>
                    <template #input>
                        <text-input placeholder="https://www." />
                    </template>
                </input-group>
                <input-group
                    v-else
                    v-model="form.brand"
                    :schema="object().required()"
                >
                    <template #label>
                        <input-label>
                            Website
                        </input-label>
                    </template>
                    <template #input>
                        <brand-select-input />
                    </template>
                </input-group>
            </flex-container>

            <!-- right side of form -->
            <flex-container
                :screen-sizes="{
                    tablet: 4,
                    laptop: 3,
                }"
            >
                <input-group
                    v-if="hasWinckBranding()"
                    v-model="form.brand"
                    :schema="object().required()"
                >
                    <template #label>
                        <input-label>
                            {{ t(`brand`) }}
                        </input-label>
                    </template>
                    <template #input>
                        <brand-select-input />
                    </template>
                </input-group>

                <input-group v-model="form.street" :schema="string().required()">
                    <template #label>
                        <input-label>
                            Straat
                        </input-label>
                    </template>
                    <template #input>
                        <text-input :disabled="disableAddress" />
                    </template>
                </input-group>

                <input-group v-model="form.phoneNumber" :schema="string().required()">
                    <template #label>
                        <input-label>
                            Telefoonnummer
                        </input-label>
                    </template>
                    <template #input>
                        <text-input />
                    </template>
                </input-group>

                <input-group v-model="form.email" :schema="string().email().required()">
                    <template #label>
                        <input-label>
                            E-mail adres
                        </input-label>
                    </template>
                    <template #input>
                        <text-input />
                    </template>
                </input-group>
            </flex-container>
        </flex-container>

        <!-- team social media -->
        <flex-container>
            <flex-container
                :screen-sizes="{
                    laptop: 2,
                }"
            >
                <data-group>
                    <data-label>
                        Social media
                    </data-label>
                    <data-value>
                        Geef de profielen op
                    </data-value>
                </data-group>
            </flex-container>

            <flex-container
                :screen-sizes="{
                    laptop: 8,
                }"
                align-content="center"
                align-items="center"
            >
                <span>Ingevuld:</span>

                <flex-container
                    inline
                    no-wrap
                    size="auto"
                >
                    <template v-for="social in Object.keys(form.socialMedia)">
                        <svg-icon
                            v-if="form.socialMedia[social]"
                            :key="social"
                            :icon="socialMediaIconMap[social]"
                            is-custom-icon
                        />
                    </template>
                </flex-container>

                <manage-social-media-button
                    v-if="socialMediaForm"
                    @click="socialMediaForm.overlay.openOverlay()"
                />

                <SocialMediaForm
                    ref="socialMediaForm"
                    @cancel-form="setSocialMediaForm"
                    @confirm-form="mergeSocialMediaForm"
                />
                <!-- social media modal here -->
            </flex-container>
        </flex-container>

        <horizontal-line
            v-if="hasWinckBranding()"
            no-margin
        />

        <!-- team email and realworks -->
        <flex-container
            v-if="hasWinckBranding()"
        >
            <flex-container
                :screen-sizes="{
                    laptop: 2,
                }"
            >
                <data-group>
                    <data-label>
                        E-mail koppelen
                    </data-label>
                    <data-value>
                        Koppel je e-mailadressen
                    </data-value>
                </data-group>
            </flex-container>

            <!-- team email and realworks -->
            <flex-container
                :screen-sizes="{
                    laptop: 8,
                }"
                align-content="center"
            >
                <text-description>
                    Voor het versturen van e-mails is een koppeling met een mailbox vereist; voeg deze hier toe.<br>
                    Bovendien kun je flows instellen die reageren op gebeurtenissen binnen Realworks.<br>
                    Dit is een slimme en efficiënte methode: voeg hier je Realworks-token toe.
                </text-description>

                <flex-container>
                    <button-block
                        v-if="emailCredentialsForm"
                        :color="'green-500'"
                        without-loading
                        @click="emailCredentialsForm.overlay.openOverlay()"
                    >
                        <svg-icon
                            :icon="LinkSimpleIcon"
                            is-custom-icon
                        />
                        <span class="button">E-mail koppelen</span>
                    </button-block>

                    <EmailCredentialsForm
                        ref="emailCredentialsForm"
                        @cancel-form="setEmailCredentialsForm"
                        @confirm-form="mergeEmailCredentialsForm"
                    />

                    <button-block
                        v-if="realworksTokenForm"
                        :color="'blue-500'"
                        without-loading
                        @click="realworksTokenForm.overlay.openOverlay()"
                    >
                        <svg-icon
                            :icon="RealworksIcon"
                            is-custom-icon
                        />
                        <span class="button">Realworks koppeling</span> <span v-if="realworksTokenCount > 0">({{ realworksTokenCount }})</span>
                    </button-block>

                    <RealworksTokenForm
                        ref="realworksTokenForm"
                        @cancel-form="setRealworksForm"
                        @confirm-form="mergeRealworksForm"
                    />
                </flex-container>
            </flex-container>
        </flex-container>
    </form-validation>
</template>

<script setup lang="ts">
    import RefreshIcon from '@keyvue/one-material-icons/icons/refresh.svg?component';
    import LinkSimpleIcon from '@keyvue/one-awesome-icons/icons/regular/link-simple.svg?component';
    import RealworksIcon from '@/assets/icons/realworks.svg?component';
    import {
        FormValidation,
        type ExposedFormValidationBindings,
        TextDescription,
        FlexContainer,
        DataGroup,
        DataLabel,
        DataValue,
        InputGroup,
        InputLabel,
        TextInput,
        ButtonBlockIcon,
        ButtonBlock,
        HorizontalLine,
        SvgIcon,
    } from '@keyvue/one-ui';
    import { computed, ref, watch } from 'vue';
    import { object, string } from 'yup';
    import PostcodeAPI from '@/services/postcode-api/postcodeApi';
    import type { Brand } from '@keyvue/one-api-client';
    import ManageSocialMediaButton from '@/company/team/components/ManageSocialMediaButton.vue';
    import SocialMediaForm, { type ExposedSocialMediaForm } from '@/company/team/components/SocialMediaForm.vue';
    import FacebookIcon from '@/assets/icons/social/round/facebook.svg?component';
    import InstagramIcon from '@/assets/icons/social/square/instagram.svg?component';
    import LinkedinIcon from '@/assets/icons/social/square/linkedin.svg?component';
    import YoutubeIcon from '@/assets/icons/social/regular/youtube.svg?component';
    import XIcon from '@/assets/icons/social/square/x.svg?component';
    import PinterestIcon from '@/assets/icons/social/round/pinterest.svg?component';
    import TikTokIcon from '@/assets/icons/social/square/tiktok.svg?component';
    import VimeoIcon from '@/assets/icons/social/regular/vimeo.svg?component';
    import EmailCredentialsForm, { type ExposedEmailCredentialsForm } from '@/company/team/components/EmailCredentialsForm.vue';
    import RealworksTokenForm, { type ExposedRealworksTokenForm } from '@/company/team/components/RealworksTokenForm.vue';
    import BrandSelectInput from '@/company/brand/components/input/BrandSelectInput.vue';
    import useAppBranding from '@/services/app-branding/composables/useAppBranding';
    import { useI18n } from 'vue-i18n';
    import messages from './TeamForm.messages.json';

    const { t } = useI18n({ messages });

    export interface Form {
        name: string
        brand: Brand | null
        street: string
        houseNumber: string
        zipcode: string
        city: string
        phoneNumber: string
        email: string
        website: string
        socialMedia: {
            facebookUrl: string
            instagramUrl: string
            linkedinUrl: string
            youtubeUrl: string
            xUrl: string
            pinterestUrl: string
            tiktokUrl: string
            vimeoUrl: string
        }
        realworks: {
            relatieToken: string
            agendaToken: string
            takenToken: string
            wonenToken: string
        }
        emailCredentials: {
            imapHostName: string
            imapPort: string
            smtpHostName: string
            smtpPort: string
            username: string
            password: string
        }
    }

    export interface ExposedTeamForm {
        form: Form,
        setForm: (values: Form) => void,
        formValidation: ExposedFormValidationBindings,
    }

    const socialMediaIconMap = {
        facebookUrl: FacebookIcon,
        instagramUrl: InstagramIcon,
        linkedinUrl: LinkedinIcon,
        youtubeUrl: YoutubeIcon,
        xUrl: XIcon,
        pinterestUrl: PinterestIcon,
        tiktokUrl: TikTokIcon,
        vimeoUrl: VimeoIcon,
    };

    const exposedValidation = ref<ExposedFormValidationBindings | any>();
    const socialMediaForm = ref<ExposedSocialMediaForm>();
    const emailCredentialsForm = ref<ExposedEmailCredentialsForm>();
    const realworksTokenForm = ref<ExposedRealworksTokenForm>();
    const disableAddress = ref(true);
    const disableRefresh = ref(false);

    const form = ref<Form>({
        name: '',
        brand: null,
        street: '',
        houseNumber: '',
        zipcode: '',
        city: '',
        phoneNumber: '',
        email: '',
        website: '',
        socialMedia: {
            facebookUrl: '',
            instagramUrl: '',
            linkedinUrl: '',
            youtubeUrl: '',
            xUrl: '',
            pinterestUrl: '',
            tiktokUrl: '',
            vimeoUrl: '',
        },
        realworks: {
            relatieToken: '',
            agendaToken: '',
            takenToken: '',
            wonenToken: '',
        },
        emailCredentials: {
            imapHostName: '',
            imapPort: '',
            smtpHostName: '',
            smtpPort: '',
            username: '',
            password: '',
        },
    });

    watch(() => form.value.brand, (value) => {
        if (!value?.websiteUrl) {
            return;
        }

        if (!hasWinckBranding()) {
            form.value.website = value.websiteUrl;
        }
    });

    const { hasWinckBranding } = useAppBranding();

    const realworksTokenCount = computed(
        () => Object.values(form.value.realworks)
            .filter((value) => value !== '')
            .length,
    );

    function setSocialMediaForm() {
        if (socialMediaForm.value) {
            socialMediaForm.value?.setForm(
                {
                    ...form.value.socialMedia,
                },
            );
        }
    }

    function setRealworksForm() {
        if (realworksTokenForm.value) {
            realworksTokenForm.value?.setForm(
                {
                    ...form.value.realworks,
                },
            );
        }
    }

    function setEmailCredentialsForm() {
        if (emailCredentialsForm.value) {
            emailCredentialsForm.value?.setForm(
                {
                    ...form.value.emailCredentials,
                },
            );
        }
    }

    function setForm(values: Form) {
        form.value = values;

        setSocialMediaForm();
        setRealworksForm();
        setEmailCredentialsForm();
    }

    function mergeSocialMediaForm() {
        if (socialMediaForm.value === undefined) {
            return;
        }

        form.value.socialMedia = {
            ...socialMediaForm.value.form,
        };
    }

    function mergeRealworksForm() {
        if (realworksTokenForm.value === undefined) {
            return;
        }

        form.value.realworks = {
            ...realworksTokenForm.value.form,
        };
    }

    function mergeEmailCredentialsForm() {
        if (emailCredentialsForm.value === undefined) {
            return;
        }

        form.value.emailCredentials = {
            ...emailCredentialsForm.value.form,
        };
    }

    async function searchForZipCode() {
        if (!form.value.zipcode || !form.value.houseNumber) {
            return;
        }

        disableRefresh.value = true;

        try {
            const response = await PostcodeAPI.lookup(form.value.zipcode, form.value.houseNumber);
            form.value.city = response.city.label ?? '';
            form.value.street = response.street;
            disableAddress.value = true;
        } catch (e) {
            disableAddress.value = false;
            disableRefresh.value = false;
        }

        disableRefresh.value = false;
    }

    defineExpose({
        form,
        setForm,
        formValidation: exposedValidation,
    });
</script>
